import { GameWorld } from "./GameWorld.tsx";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { advancePremiumJourney, initialPremiumJourney, premiumGuidePoint, type PremiumGameId } from "../shared/premium-game.ts";
import { GentleGameArena, GentleGameIntro, GentleGameResult } from "./GentleGameFrame.tsx";
import { useGameAudio } from "./gameAudio.ts";
import { useLocale } from "./i18n.tsx";
import { PlusGameGate } from "./PlusGameGate.tsx";
import type { Session } from "./Shell.tsx";
import { useGentleGame } from "./useGentleGame.ts";

const TEXT = {
  "zh-CN": {
    games: {
      lighthouse: { eyebrow: "宽幅左右 · 约 5 分钟", title: "灯塔巡航", lead: "你是今晚的灯塔守护者。轻轻转头，把灯光照向海上的小船，送它们平安归港。旅程按你的舒适范围展开，中途会两次回到中央休息。", preview: "灯塔光束缓慢扫过海面", cue: "把灯光照向海上的纸船", resultEyebrow: "灯火归航", resultTitle: "海面重新安静了" },
      "cloud-trail": { eyebrow: "连续跟随 · 约 5 分钟", title: "云谷航线", lead: "驾驶滑翔机穿过云门，发现云海中的四座浮岛。轻轻转头、抬头或低头就能掌舵，偏离时旅程会停下来等你。", preview: "一条穿过云谷的柔和航线", cue: "驾驶滑翔机，穿过前方的云门", resultEyebrow: "穿过云谷", resultTitle: "航线顺利完成" },
      "star-path": { eyebrow: "方向序列 · 约 5 分钟", title: "星图秘径", lead: "让星舟接住星光，再带回中央。一笔笔点亮星图，留下今晚的探索足迹。方向会变化，幅度不会越变越大。", preview: "星光依次点亮一条安静路径", cue: "让星舟接住星光，再带回中央", resultEyebrow: "星图点亮", resultTitle: "今晚的路径完整了" },
    },
    rest: "回到中央，休息一会儿", paused: "旅程已暂停", continue: "准备好后再继续", waiting: "慢慢靠近，旅程在等你", detail: (remaining: string) => `约剩 ${remaining} · 随时可以停止`, complete: (seconds: number) => `你用约 ${Math.max(1, Math.round(seconds / 60))} 分钟完成了这段舒适旅程。现在回到自然位置，眨眨眼、放松肩膀。`,
  },
  en: {
    games: {
      lighthouse: { eyebrow: "Wider turns · about 5 minutes", title: "Lighthouse Sweep", lead: "Be the lighthouse keeper tonight. Turn gently to shine your beam on passing boats and guide them home. The journey uses your comfortable range, with two rests at the centre.", preview: "A lighthouse beam moving slowly across the water", cue: "Shine your light on the boat", resultEyebrow: "Light safely home", resultTitle: "The water is calm again" },
      "cloud-trail": { eyebrow: "Continuous follow · about 5 minutes", title: "Cloud Valley Trail", lead: "Pilot a paper glider through cloud gates and discover four floating islands. Gentle head movements steer your wings. The journey waits whenever you drift away.", preview: "A gentle route winding through a cloud valley", cue: "Glide through the cloud gate ahead", resultEyebrow: "Across the valley", resultTitle: "The journey is complete" },
      "star-path": { eyebrow: "Direction sequence · about 5 minutes", title: "Starlight Path", lead: "Sail out to catch the starlight, then bring it back to the centre. Each journey draws another line in your constellation. The pattern changes without asking you to move farther.", preview: "Starlight revealing a quiet path", cue: "Catch the starlight and bring it home", resultEyebrow: "Constellation complete", resultTitle: "Tonight's path is glowing" },
    },
    rest: "Return to centre and rest for a moment", paused: "Journey paused", continue: "Continue whenever you feel ready", waiting: "Move gently closer. The journey is waiting", detail: (remaining: string) => `About ${remaining} left · stop whenever you need`, complete: (seconds: number) => `You completed this comfortable journey in about ${Math.max(1, Math.round(seconds / 60))} minutes. Return to a natural centre, blink, and relax your shoulders.`,
  },
} as const;

function remainingTime(elapsedMs: number) {
  const seconds = Math.max(0, Math.ceil((270_000 - elapsedMs) / 1_000));
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
}

function PremiumJourney({ game, session }: { game: PremiumGameId; session: Session | null }) {
  const { locale } = useLocale();
  const text = TEXT[locale];
  const gameText = text.games[game];
  const runtime = useGentleGame(game, session, { calibrateComfort: true });
  const audio = useGameAudio(game);
  const pointRef = useRef(runtime.point);
  pointRef.current = runtime.point;
  const journeyRef = useRef(initialPremiumJourney());
  const [journey, setJourney] = useState(journeyRef.current);
  const [paused, setPaused] = useState(false);
  const pausedRef = useRef(false);
  pausedRef.current = paused;
  const soundStepRef = useRef(0);
  const lastPaintRef = useRef(0);
  const guide = useMemo(() => premiumGuidePoint(game, journey.activeMs), [game, journey.activeMs]);
  const resting = journey.restRemainingMs > 0;
  const onCourse = Boolean(pointRef.current && Math.hypot(pointRef.current.x - guide.x, pointRef.current.y - guide.y) <= 0.3);
  const completedRestMs = journey.nextRestIndex * 15_000 - journey.restRemainingMs;
  const elapsedMs = journey.activeMs + completedRestMs;

  useEffect(() => {
    if (runtime.stage !== "intro") return;
    journeyRef.current = initialPremiumJourney();
    setJourney(journeyRef.current);
    setPaused(false);
    soundStepRef.current = 0;
    lastPaintRef.current = 0;
  }, [runtime.stage]);

  useEffect(() => {
    if (runtime.stage !== "playing") return;
    let animation = 0;
    let last = performance.now();
    const tick = (now: number) => {
      const delta = Math.min(100, now - last);
      last = now;
      const current = journeyRef.current;
      const currentGuide = premiumGuidePoint(game, current.activeMs);
      const point = pointRef.current;
      const close = Boolean(point && Math.hypot(point.x - currentGuide.x, point.y - currentGuide.y) <= 0.3);
      const next = pausedRef.current ? current : advancePremiumJourney(current, delta, close);
      if (next !== current) {
        if (next.nextRestIndex > current.nextRestIndex) audio.play("rest");
        const soundStep = Math.floor(next.activeMs / 12_000);
        if (soundStep > soundStepRef.current && next.restRemainingMs === 0) audio.play("arrive");
        soundStepRef.current = soundStep;
        journeyRef.current = next;
        const stateChanged = next.complete || next.nextRestIndex !== current.nextRestIndex || (current.restRemainingMs > 0) !== (next.restRemainingMs > 0);
        if (stateChanged || now - lastPaintRef.current >= 80) {
          lastPaintRef.current = now;
          setJourney(next);
        }
      }
      if (next.complete) {
        audio.play("complete");
        runtime.complete();
        return;
      }
      animation = requestAnimationFrame(tick);
    };
    animation = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(animation);
  }, [audio.play, game, runtime.complete, runtime.stage]);

  const togglePause = useCallback(() => setPaused((value) => !value), []);
  useEffect(() => { if (paused || runtime.stage === "intro") audio.stop(); }, [audio.stop, paused, runtime.stage]);
  useEffect(() => {
    if (runtime.stage !== "playing") return;
    const onKeyDown = (event: KeyboardEvent) => { if (event.key === "Escape") togglePause(); };
    const pauseWhenAway = () => setPaused(true);
    const pauseWhenHidden = () => { if (document.visibilityState === "hidden") pauseWhenAway(); };
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("blur", pauseWhenAway);
    document.addEventListener("visibilitychange", pauseWhenHidden);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("blur", pauseWhenAway);
      document.removeEventListener("visibilitychange", pauseWhenHidden);
    };
  }, [runtime.stage, togglePause]);

  const premiumRuntime = useMemo(() => ({
    ...runtime,
    startCamera: async () => { audio.unlock(); await runtime.startCamera(); },
    startManual: () => { audio.unlock(); runtime.startManual(); },
    reset: () => { audio.stop(); runtime.reset(); },
  }), [audio.stop, audio.unlock, runtime]);

  const preview = <GameWorld game={game} progress={.35} guide={{ x: .4, y: -.1 }} cursor={{ x: .36, y: -.1 }} />;
  if (runtime.stage === "intro") return <GentleGameIntro runtime={premiumRuntime} eyebrow={gameText.eyebrow} title={gameText.title} lead={gameText.lead} preview={preview} previewLabel={gameText.preview} tone={game} />;
  if (runtime.stage === "complete") return <GentleGameResult runtime={runtime} eyebrow={gameText.resultEyebrow} title={gameText.resultTitle} body={text.complete(runtime.elapsed)} art={<GameWorld game={game} progress={1} />} tone={game} />;
  const playingTitle = paused ? text.paused : resting ? text.rest : onCourse ? gameText.cue : text.waiting;
  const playingDetail = paused ? text.continue : text.detail(remainingTime(elapsedMs));
  return <GentleGameArena runtime={premiumRuntime} progress={Math.min(1, elapsedMs / 270_000)} arenaLabel={gameText.preview} playingTitle={playingTitle} playingDetail={playingDetail} className={`premium-game premium-${game}-game`} controls={{ soundEnabled: audio.enabled, toggleSound: audio.toggle, paused, togglePause }}>
    <GameWorld game={game} guide={guide} cursor={runtime.cursor} resting={resting} onCourse={onCourse} progress={journey.activeMs / 240_000} active={runtime.stage === "playing"} paused={paused} />
  </GentleGameArena>;
}

export function PremiumJourneyPage({ game, session }: { game: PremiumGameId; session: Session | null }) {
  return <PlusGameGate session={session}><PremiumJourney game={game} session={session} /></PlusGameGate>;
}
